import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ShieldCheck, X, AlertCircle, Loader2, RefreshCw } from 'lucide-react';

interface RecaptchaGateProps {
  isOpen: boolean;
  onSuccess: () => void;
  onCancel: () => void;
}

const MAX_ATTEMPTS = 3;

export function RecaptchaGate({ isOpen, onSuccess, onCancel }: RecaptchaGateProps) {
  const widgetIdRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setHasError(false);
      setErrorMessage(null);
      setAttempts(0);
      setIsVerifying(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !containerRef.current) return;
    
    let widgetLoaded = false;
    let renderTimeoutId: ReturnType<typeof setTimeout>;

    const initWidget = () => {
      const grecaptcha = window.grecaptcha?.enterprise;
      
      console.log('[RecaptchaGate] 🔍 Checking grecaptcha.enterprise:', {
        hasGrecaptcha: !!window.grecaptcha,
        hasEnterprise: !!grecaptcha,
        hasRender: !!grecaptcha?.render,
        container: !!containerRef.current,
      });
      
      if (grecaptcha && grecaptcha.render && containerRef.current) {
        containerRef.current.innerHTML = '';
        
        try {
          widgetIdRef.current = grecaptcha.render(containerRef.current, {
            sitekey: window.RECAPTCHA_SITE_KEY,
            size: 'normal',
            callback: async (token: string) => {
              console.log('[RecaptchaGate] ✅ Visible reCAPTCHA Enterprise passed');
              setIsVerifying(true);
              setHasError(false);
              
              try {
                const response = await fetch(
                  `${import.meta.env.VITE_SUPABASE_URL || 'https://cnpfxdcvkehjxtvlzxrp.supabase.co'}/functions/v1/verify-recaptcha`,
                  {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token }),
                  }
                );
                const result = await response.json();
                
                if (result.success) {
                  onSuccess();
                } else if (result.requiresChallenge) {
                  // Borderline score - give user another chance
                  console.log('[RecaptchaGate] ⚠️ Additional verification required');
                  setHasError(true);
                  setErrorMessage(result.message || 'Additional verification required. Please try again carefully.');
                  setAttempts(prev => prev + 1);
                  
                  if (widgetIdRef.current !== null) {
                    grecaptcha.reset(widgetIdRef.current);
                  }
                } else {
                  console.error('[RecaptchaGate] ❌ Verification failed:', result);
                  setHasError(true);
                  setErrorMessage('Verification failed. Please try again.');
                  setAttempts(prev => prev + 1);
                  
                  if (widgetIdRef.current !== null) {
                    grecaptcha.reset(widgetIdRef.current);
                  }
                }
              } catch (error) {
                console.error('[RecaptchaGate] ❌ Verification error:', error);
                // On network error, allow through (fail-open for UX)
                onSuccess();
              } finally {
                setIsVerifying(false);
              }
            },
            'error-callback': () => {
              console.error('[RecaptchaGate] ❌ Widget error');
              setHasError(true);
              setAttempts(prev => prev + 1);
            },
            'expired-callback': () => {
              console.log('[RecaptchaGate] ⏰ Token expired, resetting...');
              if (widgetIdRef.current !== null) {
                grecaptcha.reset(widgetIdRef.current);
              }
            },
          });
          widgetLoaded = true;
          console.log('[RecaptchaGate] 📦 Enterprise visible widget rendered, ID:', widgetIdRef.current);
        } catch (error) {
          console.error('[RecaptchaGate] ❌ Failed to render widget:', error);
          onSuccess();
        }
      }
    };

    // ✅ FAIL-SAFE: If widget doesn't render in 10 seconds, allow through
    renderTimeoutId = setTimeout(() => {
      if (!widgetLoaded) {
        console.warn('[RecaptchaGate] ⏰ Widget render timeout (10s) - allowing through');
        onSuccess();
      }
    }, 10000);

    if (window.grecaptcha?.enterprise?.render) {
      initWidget();
    } else {
      const interval = setInterval(() => {
        if (window.grecaptcha?.enterprise?.render) {
          clearInterval(interval);
          initWidget();
        }
      }, 100);
      
      return () => {
        clearInterval(interval);
        clearTimeout(renderTimeoutId);
      };
    }
    
    return () => clearTimeout(renderTimeoutId);
  }, [isOpen, onSuccess]);

  const handleRetry = () => {
    setHasError(false);
    setErrorMessage(null);
    const grecaptcha = window.grecaptcha?.enterprise;
    if (widgetIdRef.current !== null && grecaptcha) {
      grecaptcha.reset(widgetIdRef.current);
    }
  };

  const remainingAttempts = MAX_ATTEMPTS - attempts;
  const isBlocked = attempts >= MAX_ATTEMPTS;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />
            Security Verification
          </DialogTitle>
          <DialogDescription>
            Please complete the verification below to continue.
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex flex-col items-center py-6 space-y-4">
          {/* Error Alert */}
          {hasError && !isBlocked && (
            <Alert variant="destructive" className="w-full">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="flex flex-col gap-2">
                <span>
                  {errorMessage || 'Verification failed.'} 
                </span>
                <span className="text-xs opacity-80">
                  {remainingAttempts} {remainingAttempts === 1 ? 'attempt' : 'attempts'} remaining.
                </span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleRetry}
                  className="w-fit mt-1"
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Try Again
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Blocked Alert */}
          {isBlocked && (
            <Alert className="w-full border-muted">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Too many failed attempts. Please refresh the page and try again, or contact support if the issue persists.
              </AlertDescription>
            </Alert>
          )}

          {/* Verifying State */}
          {isVerifying && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Verifying...
            </div>
          )}

          {/* reCAPTCHA Widget */}
          {!isBlocked && (
            <div 
              ref={containerRef}
              className="min-h-[78px] flex items-center justify-center"
            />
          )}
        </div>

        <div className="flex justify-end">
          <Button variant="ghost" onClick={onCancel}>
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
